<!-- // GK-HUB: Replaced entire ContactInfo component with ISP-styled contact header (centered avatar with gradient, status dot, formatted BR phone) -->
<script>
import { useAlert } from 'dashboard/composables';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { useAdmin } from 'dashboard/composables/useAdmin';
import EditContact from './EditContact.vue';
import ContactMergeModal from 'dashboard/modules/contact/ContactMergeModal.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

import {
  isAConversationRoute,
  isAInboxViewRoute,
  getConversationDashboardRoute,
} from '../../../../helper/routeHelpers';

export default {
  components: {
    NextButton,
    EditContact,
    ContactMergeModal,
  },
  props: {
    contact: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['panelClose'],
  setup() {
    const { isAdmin } = useAdmin();
    return {
      isAdmin,
    };
  },
  data() {
    return {
      showEditModal: false,
      showMergeModal: false,
      showDeleteModal: false,
    };
  },
  computed: {
    contactProfileLink() {
      return `/app/accounts/${this.$route.params.accountId}/contacts/${this.contact.id}`;
    },
    additionalAttributes() {
      return this.contact.additional_attributes || {};
    },
    contactInitials() {
      const name = this.contact.name || '';
      return name
        .split(' ')
        .map(w => w[0])
        .filter(Boolean)
        .slice(0, 2)
        .join('')
        .toUpperCase();
    },
    // GK-HUB: Show ISP connection status from ERP instead of widget presence
    currentConversation() {
      return this.$store.getters.getSelectedChat || {};
    },
    connectionStatus() {
      return this.currentConversation?.custom_attributes?.status_conexao || '';
    },
    hasConnectionStatus() {
      return !!this.connectionStatus;
    },
    connectionStatusConfig() {
      const map = {
        online: {
          label: 'Online',
          dot: 'bg-n-teal-9 shadow-[0_0_8px_rgba(93,173,226,0.4)]',
        },
        offline: {
          label: 'Offline',
          dot: 'bg-n-ruby-9 shadow-[0_0_8px_rgba(239,68,68,0.4)]',
        },
        bloqueado: {
          label: 'Bloqueado',
          dot: 'bg-n-ruby-9 shadow-[0_0_8px_rgba(239,68,68,0.4)]',
        },
        instavel: {
          label: 'Instável',
          dot: 'bg-n-amber-9 shadow-[0_0_8px_rgba(245,158,11,0.4)]',
        },
      };
      return map[this.connectionStatus.toLowerCase()] || null;
    },
    // GK-HUB: end ISP connection status
    formattedPhone() {
      const phone = this.contact.phone_number;
      if (!phone) return '';
      // Clean and format BR phone
      const clean = phone.replace(/\D/g, '');
      if (clean.length === 13 && clean.startsWith('55')) {
        const ddd = clean.slice(2, 4);
        const num = clean.slice(4);
        if (num.length === 9) {
          return `(${ddd}) ${num.slice(0, 5)}-${num.slice(5)}`;
        }
      }
      return phone;
    },
    // Delete Modal
    confirmDeleteMessage() {
      return ` ${this.contact.name}?`;
    },
  },
  watch: {
    'contact.id': {
      handler(id) {
        this.$store.dispatch('contacts/fetchContactableInbox', id);
      },
      immediate: true,
    },
  },
  methods: {
    dynamicTime,
    toggleEditModal() {
      this.showEditModal = !this.showEditModal;
    },
    toggleDeleteModal() {
      this.showDeleteModal = !this.showDeleteModal;
    },
    confirmDeletion() {
      this.deleteContact(this.contact);
      this.closeDelete();
    },
    closeDelete() {
      this.showDeleteModal = false;
      this.showEditModal = false;
    },
    async deleteContact({ id }) {
      try {
        await this.$store.dispatch('contacts/delete', id);
        this.$emit('panelClose');
        useAlert(this.$t('DELETE_CONTACT.API.SUCCESS_MESSAGE'));

        if (isAConversationRoute(this.$route.name)) {
          this.$router.push({
            name: getConversationDashboardRoute(this.$route.name),
          });
        } else if (isAInboxViewRoute(this.$route.name)) {
          this.$router.push({
            name: 'inbox_view',
          });
        } else if (this.$route.name !== 'contacts_dashboard') {
          this.$router.push({
            name: 'contacts_dashboard',
          });
        }
      } catch (error) {
        useAlert(
          error.message
            ? error.message
            : this.$t('DELETE_CONTACT.API.ERROR_MESSAGE')
        );
      }
    },
    closeMergeModal() {
      this.showMergeModal = false;
    },
    openMergeModal() {
      this.showMergeModal = true;
    },
  },
};
</script>

<template>
  <div class="relative w-full border-b border-n-weak">
    <!-- Contact header - centered layout matching ISP preview -->
    <div class="flex flex-col items-center py-5 px-4">
      <!-- Avatar with gradient -->
      <div class="isp-contact-avatar">
        <img
          v-if="contact.thumbnail"
          :src="contact.thumbnail"
          :alt="contact.name"
          class="w-full h-full rounded-full object-cover"
        />
        <span v-else class="text-xl font-bold text-white select-none">
          {{ contactInitials }}
        </span>
      </div>

      <!-- Name -->
      <h3
        class="mt-2.5 mb-1 text-[15px] font-semibold text-n-slate-12 text-center break-words max-w-full"
      >
        {{ contact.name }}
      </h3>

      <!-- GK-HUB: ISP connection status from ERP (hides if not set) -->
      <div
        v-if="hasConnectionStatus && connectionStatusConfig"
        class="flex items-center gap-1.5 text-xs text-n-slate-10"
      >
        <span
          class="w-2 h-2 rounded-full shrink-0"
          :class="connectionStatusConfig.dot"
        />
        {{ connectionStatusConfig.label }}
      </div>
      <!-- GK-HUB: end ISP connection status -->

      <!-- Phone & Email -->
      <div class="flex items-center justify-center gap-4 mt-2.5">
        <div v-if="contact.phone_number" class="flex items-center gap-1">
          <svg
            class="w-3.5 h-3.5 text-n-slate-10 shrink-0"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path
              d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"
            />
          </svg>
          <span class="text-[11px] text-n-slate-10">{{ formattedPhone }}</span>
        </div>
        <div v-if="contact.email" class="flex items-center gap-1">
          <svg
            class="w-3.5 h-3.5 text-n-slate-10 shrink-0"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path
              d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
            />
            <polyline points="22,6 12,13 2,6" />
          </svg>
          <span class="text-[11px] text-n-slate-10">{{ contact.email }}</span>
        </div>
      </div>

      <!-- Edit button - subtle, top right -->
      <NextButton
        v-tooltip.top="$t('EDIT_CONTACT.BUTTON_LABEL')"
        icon="i-ph-pencil-simple"
        slate
        ghost
        xs
        class="absolute top-3 right-3"
        @click="toggleEditModal"
      />
    </div>

    <!-- Modals (kept for functionality) -->
    <EditContact
      v-if="showEditModal"
      :show="showEditModal"
      :contact="contact"
      @cancel="toggleEditModal"
    />
    <ContactMergeModal
      v-if="showMergeModal"
      :primary-contact="contact"
      :show="showMergeModal"
      @close="closeMergeModal"
    />
    <woot-delete-modal
      v-if="showDeleteModal"
      v-model:show="showDeleteModal"
      :on-close="closeDelete"
      :on-confirm="confirmDeletion"
      :title="$t('DELETE_CONTACT.CONFIRM.TITLE')"
      :message="$t('DELETE_CONTACT.CONFIRM.MESSAGE')"
      :message-value="confirmDeleteMessage"
      :confirm-text="$t('DELETE_CONTACT.CONFIRM.YES')"
      :reject-text="$t('DELETE_CONTACT.CONFIRM.NO')"
    />
  </div>
</template>

<style scoped>
.isp-contact-avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, #1b6b93, #5dade2);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
</style>
